import { useEffect, useRef, type ReactNode } from 'react'

interface Props {
  /** Heading shown in the sticky modal header. */
  title: ReactNode
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Max card width in px. Default 640. */
  maxWidth?: number | string // px, or any CSS width (e.g. 'min(85vw, 1400px)')
  /** Base test id: the overlay gets `${testId}`, the close button
   *  `${testId}-close`, and the scrollable body `${testId}-body`. */
  testId?: string
  /** Accessible label for the dialog. */
  ariaLabel?: string
  /** Whether clicking the backdrop closes the modal. Default true. */
  closeOnOverlayClick?: boolean
  /** Omit the header × for blocking dialogs whose own buttons are the only exit. */
  hideClose?: boolean
}

/**
 * Generic modal shell: fixed overlay, click-outside + Escape to close, a
 * sticky header with a close button, and a scrollable body that caps to the
 * viewport for long content. The single source of modal chrome — feature
 * modals (HelpModal, the AWS IAM-setup help) render their content as children.
 */
export function Modal({ title, open, onClose, children, maxWidth = 640, testId = 'modal', ariaLabel, closeOnOverlayClick = true, hideClose = false }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
        return
      }
      // Backspace/Delete with focus on a button, checkbox, or <select> (e.g. the
      // freshly-focused close button when the modal opens) triggers the browser's
      // legacy "navigate back" behavior, which unmounts the modal mid-task. Swallow
      // it unless focus is in a field where the key actually edits text.
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const t = e.target as HTMLElement | null
        const NON_TEXT_INPUT = ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']
        const editsText =
          !!t &&
          (t.tagName === 'TEXTAREA' ||
            t.isContentEditable ||
            (t.tagName === 'INPUT' && !NON_TEXT_INPUT.includes((t as HTMLInputElement).type)))
        if (!editsText) e.preventDefault()
      }
    }
    document.addEventListener('keydown', onKey)
    closeRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={closeOnOverlayClick ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '4rem 1rem',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          color: 'var(--fg)',
          borderRadius: '10px',
          maxWidth,
          width: '100%',
          // Cap the card so long bodies don't overflow the viewport — the card
          // is a flex column with a sticky header and a scrollable body.
          maxHeight: 'calc(100vh - 8rem)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
          fontWeight: 'normal',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            padding: '1.25rem 1.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.1rem', lineHeight: 1.3 }}>{title}</h2>
          {!hideClose && (
            <button
              ref={closeRef}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClose()
              }}
              aria-label="Close"
              data-testid={`${testId}-close`}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--muted)',
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>

        <div
          data-testid={`${testId}-body`}
          style={{
            padding: '1rem 1.75rem 1.5rem',
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
